import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { isValueRequired } from 'components/pages/PCM/Products/utils/isValueRequired';
import { DirectSalesLineObjective, DirectSalesPricingMode } from 'components/common/types/DirectSalesCampaign.types';
import { ProductTemplate } from 'components/common/types/DirectSalesProductTemplate.types';
import {
  NewProductCreationPayload,
  ProductManagement,
  ClearNewProductCreationPayload,
  ProductTargets,
  SelectionPermission,
  ClearSingleTargetPayload,
  ProductLine,
} from './reducer.types';
import { getNewProductLineNameAndId } from './utils';

const initialLineId = '1';

const SYNCED_LINE_FIELDS = [
  'mediaType',
  'pricingMode',
  'range',
  'channels',
  'locations',
  'formats',
  'budget',
  'frames',
  'impressions',
  'frameList',
  'poi',
  'tags',
  'sot',
  'sweep',
  'networks',
  'objectives',
] as const;

const DEFAULT_OBJECTIVES = { selectionPermission: SelectionPermission.DISABLED, value: [] };

const pickFields = <T extends Record<string, unknown>>(obj: T, keys: readonly (keyof T)[]): Partial<T> =>
  Object.fromEntries(keys.map((key) => [key, obj[key]])) as Partial<T>;

export const createProductLine = (overrides?: Partial<ProductLine>): ProductLine => ({
  lineId: initialLineId,
  name: 'Line 1',
  mediaType: undefined,
  pricingMode: undefined,
  range: undefined,
  channels: undefined,
  locations: undefined,
  formats: undefined,
  budget: undefined,
  frames: undefined,
  impressions: undefined,
  frameList: undefined,
  poi: undefined,
  tags: undefined,
  sot: undefined,
  sweep: undefined,
  networks: undefined,
  objectives: {
    selectionPermission: SelectionPermission.DISABLED,
    value: [],
  },
  ...overrides,
});

export const initialProductManagementState: ProductManagement = {
  newProduct: {
    id: '',
    name: '',
    folder: '',
    mediaType: undefined,
    pricingMode: undefined,
    range: undefined,
    channels: undefined,
    locations: undefined,
    formats: undefined,
    budget: undefined,
    frames: undefined,
    impressions: undefined,
    frameList: undefined,
    poi: undefined,
    tags: undefined,
    objectives: {
      selectionPermission: SelectionPermission.DISABLED,
      value: [],
    },
  },
  productLines: [createProductLine()],
  activeLineId: initialLineId,
  selectedProducts: [],
  secondaryPanel: PlannerSecondaryPanelType.NONE,
  originalProductId: undefined,
  intendedOriginalProductState: undefined,
  expandedFolders: [],
  isSavingProduct: false,
  isReadOnly: false,
};

export const productManagementSlice = createSlice({
  name: 'productManagement',
  initialState: initialProductManagementState,
  reducers: {
    changeSelectedProducts: (state, action: PayloadAction<ProductTemplate[]>) => {
      state.selectedProducts = action.payload;
    },
    clearSelectedProductTemplates: (state) => {
      state.selectedProducts = initialProductManagementState.selectedProducts;
    },
    changeNewProductName: (state, action: PayloadAction<string>) => {
      state.newProduct.name = action.payload;
    },
    changeSelectedFolder: (state, action: PayloadAction<string>) => {
      state.newProduct.folder = action.payload;
    },
    changeNewProduct: (state, action: PayloadAction<ProductManagement['newProduct']>) => {
      state.newProduct = action.payload;
    },
    changeNewProductField: (state, action: PayloadAction<NewProductCreationPayload>) => {
      const { selectedPermission, fieldName, value } = action.payload;

      const currentNewProduct = { ...state.newProduct };
      const updatedNewProduct = {
        ...currentNewProduct,
        [fieldName]: {
          selectionPermission: selectedPermission.code,
          value: isValueRequired(selectedPermission.code) || fieldName === 'objectives' ? value : undefined,
        },
      };

      state.newProduct = updatedNewProduct;
    },
    changeNewProductTargets: (state, action: PayloadAction<ProductTargets>) => {
      state.newProduct.budget = action.payload.budget;
      state.newProduct.frames = action.payload.frames;
      state.newProduct.impressions = action.payload.impressions;
    },
    changeNetworkAndPricingMode: (state, action: PayloadAction<NewProductCreationPayload>) => {
      const { selectedPermission, fieldName, value } = action.payload;

      const currentNewProduct = { ...state.newProduct };
      let updatedNewProduct = {
        ...currentNewProduct,
        [fieldName]: {
          selectionPermission: selectedPermission.code,
          value: isValueRequired(selectedPermission.code) ? value : undefined,
        },
      };

      if (isValueRequired(selectedPermission.code)) {
        updatedNewProduct = {
          ...updatedNewProduct,
          pricingMode: {
            selectionPermission: isValueRequired(selectedPermission.code)
              ? SelectionPermission.FIXED_VALUE
              : SelectionPermission.ALL_OPTIONS,
            value: isValueRequired(selectedPermission.code) ? DirectSalesPricingMode.NETWORK : undefined,
          },
        };
      } else if (
        state.newProduct.pricingMode?.value === DirectSalesPricingMode.NETWORK &&
        !isValueRequired(selectedPermission.code)
      ) {
        updatedNewProduct = {
          ...updatedNewProduct,
          pricingMode: undefined,
        };
      }

      state.newProduct = updatedNewProduct;
    },
    clearNetworkAndPricing: (state, action: PayloadAction<ClearNewProductCreationPayload>) => {
      const { fieldName } = action.payload;

      const currentNewProduct = { ...state.newProduct };
      const updatedNewProduct = {
        ...currentNewProduct,
        [fieldName]: undefined,
        pricingMode: undefined,
      };

      state.newProduct = updatedNewProduct;
    },
    clearSingleTarget: (state, action: PayloadAction<ClearSingleTargetPayload>) => {
      const { targetName } = action.payload;

      const currentNewProduct = { ...state.newProduct };
      const updatedNewProduct = {
        ...currentNewProduct,
        [targetName]: undefined,
      };

      state.newProduct = updatedNewProduct;
    },
    clearItemInNewProduct: (state, action: PayloadAction<ClearNewProductCreationPayload>) => {
      const { fieldName } = action.payload;

      const currentNewProduct = { ...state.newProduct };
      const updatedNewProduct = {
        ...currentNewProduct,
        [fieldName]: undefined,
      };

      state.newProduct = updatedNewProduct;
    },
    changeSecondaryPanel: (state, action: PayloadAction<PlannerSecondaryPanelType>) => {
      state.secondaryPanel = action.payload;
    },
    hideSecondaryPanel: (state) => {
      state.secondaryPanel = PlannerSecondaryPanelType.NONE;
    },
    clearNewProduct: (state) => {
      state.newProduct = initialProductManagementState.newProduct;
    },
    addObjective: (state, action: PayloadAction<DirectSalesLineObjective>) => {
      const currentObjectives = state.newProduct.objectives?.value || [];
      const newObjective = action.payload;
      const newObjectives = [...currentObjectives, newObjective];

      const currentNewProduct = { ...state.newProduct };
      const updatedNewProduct = {
        ...currentNewProduct,
        objectives: {
          ...state.newProduct.objectives,
          value: newObjectives,
        },
      };

      state.newProduct = updatedNewProduct;
    },
    updateProductObjectives: (state, action: PayloadAction<DirectSalesLineObjective[]>) => {
      state.newProduct.objectives.value = action.payload;
    },
    clearObjectivesInNewProduct: (state) => {
      const currentNewProduct = { ...state.newProduct };
      const updatedNewProduct = {
        ...currentNewProduct,
        objectives: initialProductManagementState.newProduct.objectives,
      };

      state.newProduct = updatedNewProduct;
    },
    setOriginalProduct: (
      state,
      action: PayloadAction<{ originalProductId?: string; intendedOriginalProductState?: string }>,
    ) => {
      state.originalProductId = action.payload.originalProductId;
      state.intendedOriginalProductState = action.payload.intendedOriginalProductState;
    },
    setExpandedFolders: (state, action: PayloadAction<string[]>) => {
      state.expandedFolders = action.payload;
    },
    setIsSavingProduct: (state, action: PayloadAction<boolean>) => {
      state.isSavingProduct = action.payload;
    },
    setIsReadOnly: (state, action: PayloadAction<boolean>) => {
      state.isReadOnly = action.payload;
    },
    addProductLine: (state) => {
      const { lineId, name } = getNewProductLineNameAndId(state.productLines);
      const newLine = createProductLine({ lineId, name });
      state.productLines.push(newLine);
      state.activeLineId = lineId;
    },
    duplicateProductLine: (state) => {
      const activeLineCopy = structuredClone(
        current(state.productLines.find((line) => line.lineId === state.activeLineId)),
      );

      if (!activeLineCopy) return;

      const { lineId, name } = getNewProductLineNameAndId(state.productLines);
      activeLineCopy.lineId = lineId;
      activeLineCopy.name = name;
      state.productLines.push(activeLineCopy);
      state.activeLineId = lineId;
    },
    removeProductLine: (state, action: PayloadAction<string>) => {
      const lineIdToRemove = action.payload;
      const lineIndex = state.productLines.findIndex((line) => line.lineId === lineIdToRemove);

      if (state.productLines.length <= 1) return;

      state.productLines = state.productLines.filter((line) => line.lineId !== lineIdToRemove);

      if (state.activeLineId === lineIdToRemove) {
        const newActiveIndex = Math.min(lineIndex, state.productLines.length - 1);
        state.activeLineId = state.productLines[newActiveIndex].lineId;
      }
    },
    changeProductLineName: (state, action: PayloadAction<{ lineId: string; name: string }>) => {
      const line = state.productLines.find((l) => l.lineId === action.payload.lineId);
      if (line) {
        line.name = action.payload.name;
      }
    },
    changeActiveProductLineId: (state, action: PayloadAction<string>) => {
      const lineExists = state.productLines.some((line) => line.lineId === action.payload);
      if (lineExists) {
        state.activeLineId = action.payload;
      }
    },
    clearProductLines: (state) => {
      state.productLines = initialProductManagementState.productLines;
      state.activeLineId = initialProductManagementState.activeLineId;
    },
    setProductLines: (state, action: PayloadAction<ProductLine[]>) => {
      state.productLines = action.payload;
      if (action.payload.length > 0) {
        state.activeLineId = action.payload[0].lineId;
      }
    },
    syncActiveLineFromNewProduct: (state) => {
      const activeLineIndex = state.productLines.findIndex((line) => line.lineId === state.activeLineId);
      if (activeLineIndex === -1) return;

      const syncedFields = pickFields(state.newProduct, SYNCED_LINE_FIELDS);
      state.productLines[activeLineIndex] = {
        ...state.productLines[activeLineIndex],
        ...syncedFields,
        objectives: syncedFields.objectives ?? DEFAULT_OBJECTIVES,
      };
    },
    syncNewProductFromActiveLine: (state) => {
      const activeLine = state.productLines.find((line) => line.lineId === state.activeLineId);
      if (!activeLine) return;

      const syncedFields = pickFields(activeLine, SYNCED_LINE_FIELDS);
      state.newProduct = {
        ...state.newProduct,
        ...syncedFields,
        objectives: syncedFields.objectives ?? DEFAULT_OBJECTIVES,
      };
    },
  },
});

export const {
  changeSelectedProducts,
  clearSelectedProductTemplates,
  changeNewProduct,
  changeNewProductField,
  changeSelectedFolder,
  changeNewProductTargets,
  changeNewProductName,
  changeSecondaryPanel,
  hideSecondaryPanel,
  clearItemInNewProduct,
  clearNewProduct,
  clearSingleTarget,
  changeNetworkAndPricingMode,
  clearNetworkAndPricing,
  addObjective,
  updateProductObjectives,
  setOriginalProduct,
  clearObjectivesInNewProduct,
  setExpandedFolders,
  setIsSavingProduct,
  setIsReadOnly,
  addProductLine,
  duplicateProductLine,
  removeProductLine,
  changeProductLineName,
  changeActiveProductLineId,
  clearProductLines,
  setProductLines,
  syncActiveLineFromNewProduct,
  syncNewProductFromActiveLine,
} = productManagementSlice.actions;

export default productManagementSlice.reducer;
